import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { privateHttp } from "~/config/axios";
import { API_ENDPOINTS } from "~/config/endpoints";
import {
  BusinessPhotoFormResponse,
  BusinessPhotoAlbumRequest,
  BusinessPhotoAlbumResponse,
  BusinessPhotoCreateRequest,
  BusinessPhotoCreateResponse,
  BusinessPhotoDeleteRequest,
  BusinessPhotoDeleteResponse,
  BusinessPhotoDownloadRequest,
  BusinessPhotoUploadPolicyResponse,
} from "~/types/common";

// Business Photo Form 조회
const getBusinessPhotoForm = async (): Promise<BusinessPhotoFormResponse> => {
  try {
    const { data } = await privateHttp.get(API_ENDPOINTS.BUSINESS_PHOTO.FORM);
    return data;
  } catch (error) {
    console.error("비즈니스 포토 폼 API 호출 에러:", error);
    throw error;
  }
};

export const useBusinessPhotoForm = () => {
  return useQuery({
    queryKey: ["businessPhotoForm"],
    queryFn: getBusinessPhotoForm,
    staleTime: 5 * 60 * 1000, // 5분
    gcTime: 10 * 60 * 1000, // 10분
  });
};

const getBusinessPhotoUploadPolicy =
  async (): Promise<BusinessPhotoUploadPolicyResponse> => {
    try {
      const { data } = await privateHttp.get(
        API_ENDPOINTS.BUSINESS_PHOTO.UPLOAD_POLICY
      );
      return data;
    } catch (error) {
      console.error("비즈니스 포토 업로드 정책 API 호출 에러:", error);
      throw error;
    }
  };

export const useBusinessPhotoUploadPolicy = () => {
  return useQuery({
    queryKey: ["businessPhotoUploadPolicy"],
    queryFn: getBusinessPhotoUploadPolicy,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
  });
};

// Business Photo Album 조회
const getBusinessPhotoAlbum = async (
  params: BusinessPhotoAlbumRequest
): Promise<BusinessPhotoAlbumResponse> => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.BUSINESS_PHOTO.ALBUM_PAGE,
      { params }
    );
    return data;
  } catch (error) {
    console.error("비즈니스 포토 앨범 API 호출 에러:", error);
    throw error;
  }
};

export const useBusinessPhotoAlbum = (
  params: BusinessPhotoAlbumRequest & { enabled?: boolean }
) => {
  const pageSize = params.size ?? 100;

  return useInfiniteQuery({
    queryKey: ["businessPhotoAlbum", params.searchKeyword],
    queryFn: ({ pageParam }) =>
      getBusinessPhotoAlbum({
        searchKeyword: params.searchKeyword,
        cursor: pageParam,
        size: pageSize,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.data.hasNext
        ? lastPage.data.nextCursor ?? undefined
        : undefined,
    staleTime: 2 * 60 * 1000, // 2분 - 중복 요청 방지
    gcTime: 10 * 60 * 1000, // 10분
    enabled: params.enabled !== false, // enabled가 false가 아닐 때만 활성화
    refetchOnMount: true,
  });
};

// Business Photo 생성 (진행률 콜백 및 취소 지원)
interface CreatePhotoOptions {
  petSeq: number;
  files: File[];
  muteYn?: "Y" | "N";
  onProgress?: (progress: number) => void;
  signal?: AbortSignal;
}

const createBusinessPhoto = async (
  options: CreatePhotoOptions
): Promise<BusinessPhotoCreateResponse> => {
  try {
    const formData = new FormData();
    formData.append("petSeq", options.petSeq.toString());
    formData.append("muteYn", options.muteYn ?? "N");

    // 파일들을 FormData에 추가
    options.files.forEach((file) => {
      formData.append("files", file);
    });

    const { data } = await privateHttp.post(
      API_ENDPOINTS.BUSINESS_PHOTO.CREATE,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        // 업로드 진행률 콜백
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total && options.onProgress) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            options.onProgress(percentCompleted);
          }
        },
        // 취소 시그널
        signal: options.signal,
      }
    );
    return data;
  } catch (error) {
    console.error("비즈니스 포토 생성 API 호출 에러:", error);
    throw error;
  }
};

// 진행률 및 취소를 지원하는 업로드 함수 (mutation 대신 직접 호출용)
export const uploadBusinessPhoto = createBusinessPhoto;

export const useBusinessPhotoCreate = () => {
  return useMutation({
    mutationFn: (requestData: BusinessPhotoCreateRequest) =>
      createBusinessPhoto({
        petSeq: requestData.petSeq,
        files: requestData.files,
      }),
  });
};

// Business Photo 삭제
const deleteBusinessPhoto = async (
  params: BusinessPhotoDeleteRequest
): Promise<BusinessPhotoDeleteResponse> => {
  try {
    const { data } = await privateHttp.delete(
      `${API_ENDPOINTS.BUSINESS_PHOTO.DELETE}?albumSeq=${params.albumSeq}`
    );
    return data;
  } catch (error) {
    console.error("비즈니스 포토 삭제 API 호출 에러:", error);
    throw error;
  }
};

export const useBusinessPhotoDelete = () => {
  return useMutation({
    mutationFn: deleteBusinessPhoto,
  });
};

// Business Photo 다운로드
const downloadBusinessPhoto = async (
  params: BusinessPhotoDownloadRequest
): Promise<Blob> => {
  try {
    const { data } = await privateHttp.get(
      `${API_ENDPOINTS.BUSINESS_PHOTO.DOWNLOAD}?albumSeq=${params.albumSeq}`,
      {
        responseType: "blob",
      }
    );
    return data;
  } catch (error) {
    console.error("비즈니스 포토 다운로드 API 호출 에러:", error);
    throw error;
  }
};

export const useBusinessPhotoDownload = () => {
  return useMutation({
    mutationFn: downloadBusinessPhoto,
  });
};
